import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { formsApi } from '@api/forms'
import { submissionsApi } from '@api/submissions'
import { verificationApi } from '@api/verification'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import LanguageSwitcher from '@components/Common/LanguageSwitcher'
import '@styles/form.css'

const PublicForm = () => {
    const { formId } = useParams()
    const [searchParams] = useSearchParams()

    // Check if embedded mode
    const isEmbedded = searchParams.get('embed') === 'true'

    // Use global UI store for language
    const { language, setLanguage } = useUIStore()

    // Auto-detect language only on mount if needed
    useEffect(() => {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const langParam = urlParams.get('lang')?.toLowerCase()

        let detectedLang = null

        // 1. Check for explicit lang parameter (highest priority)
        if (langParam === 'ar' || langParam === 'en') {
            detectedLang = langParam
        } else {
            // 2. Check URL path
            const path = window.location.pathname.toLowerCase()
            if (path.includes('/ar/') || path.startsWith('/ar')) {
                detectedLang = 'ar'
            } else if (path.includes('/en/') || path.startsWith('/en')) {
                detectedLang = 'en'
            }
        }

        // Only override if specific intention detected or if store is empty (unlikely)
        if (detectedLang && detectedLang !== language) {
            setLanguage(detectedLang)
        }
    }, [])
    const [formData, setFormData] = useState({})
    const [errors, setErrors] = useState({})
    const [submitted, setSubmitted] = useState(false)
    const [submissionId, setSubmissionId] = useState('')

    // Email verification state
    const [verificationState, setVerificationState] = useState({})
    const [verificationCodes, setVerificationCodes] = useState({})
    const [sendingCode, setSendingCode] = useState({})
    const [verifyingCode, setVerifyingCode] = useState({})

    const isRTL = language === 'ar'

    // Get logged-in end-user for auto-filling
    const { user } = useAuthStore()
    const isEndUser = user?.user_type === 'end_user'

    // Scroll to bottom when form is submitted successfully (so user sees the success message)
    useEffect(() => {
        if (submitted) {
            // Scroll to the bottom of the form where the success message appears
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
            }, 100)
        }
    }, [submitted])

    // Embed mode is detected but no auto-resize (fixed height in embed code)
    // This avoids the infinite loop issue with postMessage height updates

    const { data: formResponse, isLoading, isError, error } = useQuery({
        queryKey: ['publicForm', formId],
        queryFn: () => formsApi.getPublic(formId),
        enabled: !!formId,
        retry: false, // Don't retry on 401
    })

    const form = formResponse?.data

    // Parse fields if they come as string
    const fields = typeof form?.fields === 'string'
        ? JSON.parse(form.fields)
        : (form?.fields || [])

    // Auto-fill email and phone from end-user profile when form loads
    useEffect(() => {
        if (isEndUser && user && fields.length > 0) {
            const autoFillData = {}
            fields.forEach(field => {
                // Auto-fill email fields
                if (field.type === 'email' && user.email && !formData[field.id]) {
                    autoFillData[field.id] = user.email
                }
                // Auto-fill phone fields
                if (field.type === 'phone' && user.phone && !formData[field.id]) {
                    autoFillData[field.id] = user.phone
                }
                // Auto-fill name fields (matching common field IDs)
                if (field.type === 'text' && !formData[field.id]) {
                    const fieldIdLower = field.id.toLowerCase()
                    if ((fieldIdLower.includes('name') || fieldIdLower.includes('full')) && user.full_name) {
                        autoFillData[field.id] = user.full_name
                    }
                }
            })
            if (Object.keys(autoFillData).length > 0) {
                setFormData(prev => ({ ...prev, ...autoFillData }))
            }
        }
    }, [isEndUser, user, fields.length])

    // Form style settings with MoJ defaults (read from settings object with fallback)
    const settings = form?.settings || {}
    const formStyle = {
        primaryColor: settings.primary_color || form?.primary_color || '#1a3a6b',
        headerGradientEnd: settings.header_gradient_end || form?.header_gradient_end || '#2a7d62',
        backgroundColor: settings.background_color || form?.background_color || '#1a3a6b',
        buttonColor: settings.button_color || form?.button_color || '#1a3a6b',
        formWidth: settings.form_width || form?.form_width || '800',
        fontFamily: settings.font_family || form?.font_family || 'Inter, Cairo, sans-serif',
        headerLogoUrl: settings.header_logo_url || form?.header_logo_url || '',
        emailLogoUrl: settings.email_logo_url || form?.email_logo_url || ''
    }

    const submitMutation = useMutation({
        mutationFn: (data) => submissionsApi.create(parseInt(formId), data),
        onSuccess: (response) => {
            setSubmitted(true)
            setSubmissionId(response.data?.submission_id || response.submission_id || 'SUCCESS')
        },
        onError: (error) => {
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        },
    })

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }))
        if (errors[fieldId]) {
            setErrors((prev) => ({ ...prev, [fieldId]: null }))
        }
        // Reset verification if email changes
        if (verificationState[fieldId]) {
            setVerificationState((prev) => ({ ...prev, [fieldId]: null }))
        }
    }

    // Send verification code
    const handleSendCode = async (fieldId, email) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrors((prev) => ({
                ...prev,
                [fieldId]: language === 'ar' ? 'أدخل بريد إلكتروني صالح أولاً' : 'Enter a valid email first'
            }))
            return
        }

        setSendingCode((prev) => ({ ...prev, [fieldId]: true }))
        try {
            await verificationApi.sendCode(email, parseInt(formId), language)
            setVerificationState((prev) => ({ ...prev, [fieldId]: 'pending' }))
        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                [fieldId]: language === 'ar' ? 'فشل في إرسال الرمز' : 'Failed to send code'
            }))
        } finally {
            setSendingCode((prev) => ({ ...prev, [fieldId]: false }))
        }
    }

    // Verify code
    const handleVerifyCode = async (fieldId, email, code) => {
        if (!code || code.length !== 6) {
            return
        }

        setVerifyingCode((prev) => ({ ...prev, [fieldId]: true }))
        try {
            const res = await verificationApi.verifyCode(email, code, parseInt(formId))
            if (res.data?.verified) {
                setVerificationState((prev) => ({ ...prev, [fieldId]: 'verified' }))
            } else {
                setErrors((prev) => ({
                    ...prev,
                    [fieldId]: language === 'ar' ? 'رمز غير صالح' : 'Invalid code'
                }))
            }
        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                [fieldId]: language === 'ar' ? 'رمز غير صالح أو منتهي الصلاحية' : 'Invalid or expired code'
            }))
        } finally {
            setVerifyingCode((prev) => ({ ...prev, [fieldId]: false }))
        }
    }

    // Get label with conditional label support based on other field values
    const getLabel = (field) => {
        // Check for conditional labels based on another field's value
        if (field.conditionalLabels) {
            const controllingFieldValue = formData[field.conditionalLabels.field]
            if (controllingFieldValue && field.conditionalLabels.values[controllingFieldValue]) {
                const conditionalLabel = field.conditionalLabels.values[controllingFieldValue]
                return language === 'ar' ? conditionalLabel.label_ar : conditionalLabel.label_en
            }
        }
        // Default labels
        if (language === 'ar' && field.label_ar) {
            return field.label_ar
        }
        return field.label_en || field.label || field.id
    }

    const getPlaceholder = (field) => {
        if (language === 'ar' && field.placeholder_ar) {
            return field.placeholder_ar
        }
        return field.placeholder_en || field.placeholder || ''
    }

    // Check if a field should be visible based on conditionalVisibility
    const shouldShowField = (field) => {
        // If no conditional visibility, always show
        if (!field.conditionalVisibility) return true

        const { field: controllingField, showWhen } = field.conditionalVisibility
        const controllingValue = formData[controllingField]

        // If showWhen is an array, check if value is in array
        if (Array.isArray(showWhen)) {
            return showWhen.includes(controllingValue)
        }
        // If showWhen is a single value, check equality
        return controllingValue === showWhen
    }

    const validateForm = () => {
        const newErrors = {}

        fields.forEach((field) => {
            // Skip validation for hidden fields
            if (!shouldShowField(field)) return

            const value = formData[field.id]

            // Handle repeater fields validation deeply
            if (field.type === 'repeater') {
                const count = Array.isArray(value) ? value.length : 0;
                
                // If repeater is strictly required but has no items (this usually doesn't happen because trigger controls count, but just in case)
                if (field.required && count === 0) {
                    newErrors[field.id] = language === 'ar'
                        ? 'هذا الحقل مطلوب'
                        : 'This field is required'
                    return
                }

                if (count > 0 && Array.isArray(value)) {
                    let hasRepeaterError = false;
                    value.forEach((item) => {
                        (field.fields || []).forEach(subField => {
                            const subValue = item[subField.id];
                            // File fields are objects, others are strings/numbers
                            if (subValue === undefined || subValue === null || subValue.toString().trim() === '') {
                                hasRepeaterError = true;
                            }
                        });
                    });
                    
                    if (hasRepeaterError) {
                        newErrors[field.id] = language === 'ar'
                            ? 'يرجى تعبئة جميع تفاصيل الحقول الفرعية'
                            : 'Please fill out all sub-field details'
                        return
                    }
                }
            } else if (field.required && (value === undefined || value === null || value.toString().trim() === '')) {
                // Standard required validation
                newErrors[field.id] = language === 'ar'
                    ? 'هذا الحقل مطلوب'
                    : 'This field is required'
                return
            }

            if (!value) return

            // Email verification check
            if (field.type === 'email' && field.require_verification) {
                if (verificationState[field.id] !== 'verified') {
                    newErrors[field.id] = language === 'ar'
                        ? 'يرجى التحقق من البريد الإلكتروني أولاً'
                        : 'Please verify your email first'
                    return
                }
            }

            // Captcha validation
            if (field.type === 'captcha' && value) {
                const captchaKey = `captcha_${field.id}`
                const captcha = window[captchaKey]
                if (captcha && parseInt(value) !== captcha.answer) {
                    newErrors[field.id] = language === 'ar'
                        ? 'الإجابة غير صحيحة'
                        : 'Incorrect answer'
                    return
                }
            }

            // Min length validation
            if (field.min_length && value.length < field.min_length) {
                newErrors[field.id] = language === 'ar'
                    ? `الحد الأدنى للأحرف ${field.min_length}`
                    : `Minimum ${field.min_length} characters required`
            }

            // Max length validation
            if (field.max_length && value.length > field.max_length) {
                newErrors[field.id] = language === 'ar'
                    ? `الحد الأقصى للأحرف ${field.max_length}`
                    : `Maximum ${field.max_length} characters allowed`
            }

            // Email validation
            if (field.type === 'email' && value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                if (!emailRegex.test(value)) {
                    newErrors[field.id] = language === 'ar'
                        ? 'البريد الإلكتروني غير صالح'
                        : 'Invalid email format'
                }
            }

            // Pattern validation
            if (field.pattern && value) {
                const regex = new RegExp(field.pattern)
                if (!regex.test(value)) {
                    newErrors[field.id] = language === 'ar'
                        ? (field.pattern_message_ar || 'صيغة غير صالحة')
                        : (field.pattern_message_en || 'Invalid format')
                }
            }
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!validateForm()) return
        submitMutation.mutate(formData)
    }

    // Check if any email field requires verification but is not verified
    const isEmailVerificationPending = () => {
        return fields.some(field =>
            field.type === 'email' &&
            field.require_verification &&
            verificationState[field.id] !== 'verified'
        )
    }

    const getFieldWidth = (field) => {
        const width = field.width || '100'
        return `width-${width}`
    }

    const renderField = (field) => {
        const value = formData[field.id] || ''
        const error = errors[field.id]
        const label = getLabel(field)
        const placeholder = getPlaceholder(field)
        const widthClass = getFieldWidth(field)

        const fieldContent = () => {
            switch (field.type) {
                case 'textarea':
                    return (
                        <textarea
                            className={`pfb-field-textarea ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            placeholder={placeholder}
                            rows={field.rows || 4}
                        />
                    )

                case 'select':
                    // Handle allowOther: show text input when 'other' is selected
                    const isOtherSelected = field.allowOther && value === 'other'
                    const otherFieldId = `${field.id}_other`
                    const otherValue = formData[otherFieldId] || ''

                    return (
                        <>
                            <select
                                className={`pfb-field-select ${error ? 'error' : ''}`}
                                value={value}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            >
                                <option value="">{placeholder || (language === 'ar' ? 'اختر...' : 'Select...')}</option>
                                {(field.options || []).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {language === 'ar' ? opt.label_ar : opt.label_en}
                                    </option>
                                ))}
                            </select>
                            {isOtherSelected && (
                                <input
                                    type="text"
                                    className="pfb-field-input"
                                    style={{ marginTop: '8px' }}
                                    value={otherValue}
                                    onChange={(e) => handleFieldChange(otherFieldId, e.target.value)}
                                    placeholder={language === 'ar' ? (field.otherLabel_ar || 'يرجى التحديد') : (field.otherLabel_en || 'Please specify')}
                                    required={field.required}
                                />
                            )}
                        </>
                    )

                case 'radio':
                    return (
                        <div className="pfb-radio-group">
                            {(field.options || []).map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`pfb-radio-option ${isRTL ? 'rtl' : ''} ${value === opt.value ? 'selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name={field.id}
                                        value={opt.value}
                                        checked={value === opt.value}
                                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    />
                                    {language === 'ar' ? opt.label_ar : opt.label_en}
                                </label>
                            ))}
                        </div>
                    )

                case 'checkbox':
                    if (field.options && field.options.length > 0) {
                        // Multiple checkbox options
                        const selectedValues = Array.isArray(value) ? value : []
                        return (
                            <div className="pfb-checkbox-group">
                                {field.options.map((opt) => (
                                    <label
                                        key={opt.value}
                                        className={`pfb-checkbox-option ${isRTL ? 'rtl' : ''} ${selectedValues.includes(opt.value) ? 'selected' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            value={opt.value}
                                            checked={selectedValues.includes(opt.value)}
                                            onChange={(e) => {
                                                const newValues = e.target.checked
                                                    ? [...selectedValues, opt.value]
                                                    : selectedValues.filter((v) => v !== opt.value)
                                                handleFieldChange(field.id, newValues)
                                            }}
                                        />
                                        {language === 'ar' ? opt.label_ar : opt.label_en}
                                    </label>
                                ))}
                            </div>
                        )
                    } else {
                        // Single checkbox
                        return (
                            <label className={`pfb-checkbox-option ${isRTL ? 'rtl' : ''} ${value ? 'selected' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={!!value}
                                    onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                                />
                                {label}
                            </label>
                        )
                    }

                case 'file':
                    const fileInputId = `file-input-${field.id}`
                    const selectedFile = value ? (typeof value === 'string' ? value : value.name) : null
                    return (
                        <div
                            className="pfb-file-upload"
                            onClick={() => document.getElementById(fileInputId)?.click()}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="pfb-file-upload-icon">{selectedFile ? '✓' : '📎'}</div>
                            <div className="pfb-file-upload-text">
                                {selectedFile
                                    ? selectedFile
                                    : (language === 'ar' ? 'اختر ملف أو اسحبه هنا' : 'Choose a file or drag it here')}
                            </div>
                            <div className="pfb-file-upload-hint">
                                {language === 'ar'
                                    ? `الحد الأقصى: ${(field.max_size || 10485760) / 1024 / 1024}MB`
                                    : `Max size: ${(field.max_size || 10485760) / 1024 / 1024}MB`}
                            </div>
                            <input
                                id={fileInputId}
                                type="file"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFieldChange(field.id, e.target.files[0])}
                                accept={field.allowed_types?.map(t => `.${t}`).join(',') || '.jpg,.jpeg,.png,.pdf,.doc,.docx'}
                            />
                        </div>
                    )

                case 'date':
                    return (
                        <input
                            type="date"
                            className={`pfb-field-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            min={field.min_date}
                            max={field.max_date}
                        />
                    )

                case 'time':
                    return (
                        <input
                            type="time"
                            className={`pfb-field-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        />
                    )

                case 'captcha':
                    // Generate captcha question if not already set for this field
                    const captchaKey = `captcha_${field.id}`
                    if (!window[captchaKey]) {
                        const num1 = Math.floor(Math.random() * 10) + 1
                        const num2 = Math.floor(Math.random() * 10) + 1
                        window[captchaKey] = { num1, num2, answer: num1 + num2 }
                    }
                    const captcha = window[captchaKey]
                    return (
                        <div className="pfb-captcha-row">
                            <div className="pfb-captcha-question" style={{ marginBottom: '8px', fontWeight: 600 }}>
                                {captcha.num1} + {captcha.num2} = ?
                            </div>
                            <input
                                type="text"
                                className={`pfb-field-input ${error ? 'pfb-field-error' : ''}`}
                                value={value}
                                onChange={(e) => {
                                    const answer = e.target.value.replace(/\D/g, '')
                                    handleFieldChange(field.id, answer)
                                }}
                                placeholder={language === 'ar' ? 'أدخل الإجابة' : 'Enter the answer'}
                                dir="ltr"
                            />
                        </div>
                    )

                case 'number':
                    return (
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className={`pfb-field-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => {
                                // Only allow digits
                                const numericValue = e.target.value.replace(/[^0-9]/g, '')
                                handleFieldChange(field.id, numericValue)
                            }}
                            onKeyDown={(e) => {
                                // Allow backspace, delete, arrows, tab
                                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) return
                                // Block non-numeric keys
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault()
                                }
                            }}
                            placeholder={placeholder}
                        />
                    )

                case 'email':
                    const isVerified = verificationState[field.id] === 'verified'
                    const isPending = verificationState[field.id] === 'pending'
                    const needsVerification = field.require_verification
                    // Check if email has valid format (e.g., example@test.com)
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    const isValidEmail = value && emailRegex.test(value)
                    // Show verification section only when email has valid format
                    const showVerification = needsVerification && !isVerified && isValidEmail

                    return (
                        <div>
                            <input
                                type="email"
                                className={`pfb-field-input ${error ? 'error' : ''} ${isVerified ? 'verified' : ''}`}
                                value={value}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                placeholder={placeholder}
                                disabled={isVerified}
                                style={isVerified ? { borderColor: '#059669', backgroundColor: '#f0fdf4' } : {}}
                            />

                            {showVerification && (
                                <div className="pfb-email-verification">
                                    {!isPending ? (
                                        <button
                                            type="button"
                                            className="pfb-send-code-button"
                                            onClick={() => handleSendCode(field.id, value)}
                                            disabled={sendingCode[field.id]}
                                            style={{ background: formStyle.primaryColor }}
                                        >
                                            {sendingCode[field.id]
                                                ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                                                : (language === 'ar' ? 'إرسال رمز التحقق' : 'Send Verification Code')
                                            }
                                        </button>
                                    ) : (
                                        <div className="pfb-verification-step">
                                            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                                                {language === 'ar'
                                                    ? 'تم إرسال الرمز إلى بريدك الإلكتروني'
                                                    : 'Code sent to your email'}
                                            </p>
                                            <input
                                                type="text"
                                                className="pfb-verification-code-input"
                                                placeholder="000000"
                                                maxLength={6}
                                                value={verificationCodes[field.id] || ''}
                                                onChange={(e) => {
                                                    const code = e.target.value.replace(/\D/g, '').slice(0, 6)
                                                    setVerificationCodes((prev) => ({ ...prev, [field.id]: code }))
                                                    if (code.length === 6) {
                                                        handleVerifyCode(field.id, value, code)
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="pfb-send-code-button"
                                                onClick={() => handleSendCode(field.id, value)}
                                                disabled={sendingCode[field.id]}
                                                style={{ marginTop: '10px', background: '#6b7280' }}
                                            >
                                                {language === 'ar' ? 'إعادة إرسال' : 'Resend Code'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {needsVerification && isVerified && (
                                <div className="pfb-verification-status success">
                                    {language === 'ar' ? '✓ تم التحقق من البريد الإلكتروني' : '✓ Email Verified'}
                                </div>
                            )}
                        </div>
                    )

                case 'phone':
                    return (
                        <input
                            type="tel"
                            className={`pfb-field-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => {
                                // Only allow digits, plus, dash, spaces, and parentheses for phone
                                const cleaned = e.target.value.replace(/[^0-9+\-\s()]/g, '')
                                handleFieldChange(field.id, cleaned)
                            }}
                            onKeyDown={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, arrows
                                if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) return
                                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return
                                // Allow: home, end
                                if (e.keyCode >= 35 && e.keyCode <= 40) return
                                // Allow: digits, plus, dash, space, parentheses
                                if (/[0-9+\-\s()]/.test(e.key)) return
                                // Block anything else
                                e.preventDefault()
                            }}
                            placeholder={placeholder}
                            inputMode="tel"
                            style={{
                                color: '#111827',
                                fontSize: '16px',
                                WebkitTextFillColor: '#111827'
                            }}
                        />
                    )

                case 'repeater':
                    // Get count from trigger field (e.g., delegation_count)
                    const triggerValue = parseInt(formData[field.trigger_field] || '0', 10)
                    const offset = field.offset || 0 // e.g., -1 for n-1
                    const count = Math.max(0, Math.min(triggerValue + offset, field.max || 20))
                    const repeaterData = Array.isArray(formData[field.id]) ? formData[field.id] : []

                    // Ensure array has correct length
                    const ensuredData = [...repeaterData]
                    while (ensuredData.length < count) {
                        ensuredData.push({})
                    }
                    if (ensuredData.length > count) {
                        ensuredData.length = count
                    }
                    // Update formData if needed
                    if (JSON.stringify(ensuredData) !== JSON.stringify(repeaterData)) {
                        handleFieldChange(field.id, ensuredData)
                    }

                    if (count === 0) {
                        return null
                    }

                    return (
                        <div className="pfb-repeater-container" style={{
                            width: '100%',
                            marginBottom: '16px',
                            padding: '16px',
                            background: error ? 'rgba(239,68,68,0.05)' : 'rgba(0,0,0,0.02)',
                            borderRadius: '8px',
                            border: `1px dashed ${error ? 'rgba(239,68,68,0.5)' : 'rgba(0,0,0,0.1)'}`
                        }}>
                            <div className="pfb-repeater-label" style={{
                                fontWeight: 600,
                                marginBottom: '12px',
                                color: '#374151'
                            }}>
                                {label} ({count} {language === 'ar' ? 'عناصر' : 'items'})
                            </div>
                            {Array.from({ length: count }, (_, index) => (
                                <div key={`${field.id}-${index}`} className="pfb-repeater-item" style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    marginBottom: '12px',
                                    padding: '12px',
                                    background: '#fff',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{
                                        width: '100%',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        marginBottom: '4px'
                                    }}>
                                        #{index + 1}
                                    </div>
                                    {(field.fields || []).map((subField) => {
                                        const subValue = ensuredData[index]?.[subField.id] || ''
                                        const subWidth = subField.width || '50'
                                        const subFileInputId = `repeater-file-${field.id}-${index}-${subField.id}`
                                        return (
                                            <div key={subField.id} style={{
                                                flex: `0 0 calc(${subWidth}% - 6px)`,
                                                minWidth: '120px'
                                            }}>
                                                <label className="pfb-repeater-field-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    marginBottom: '6px',
                                                    color: '#1f2937'
                                                }}>
                                                    {language === 'ar' ? (subField.label_ar || subField.label_en) : (subField.label_en || subField.label_ar)}
                                                </label>
                                                {subField.type === 'file' ? (
                                                    <div
                                                        className="pfb-file-upload"
                                                        onClick={() => document.getElementById(subFileInputId)?.click()}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '8px',
                                                            border: `1px dashed ${error && (!subValue || subValue.toString().trim() === '') ? '#ef4444' : '#d1d5db'}`,
                                                            borderRadius: '6px',
                                                            background: error && (!subValue || subValue.toString().trim() === '') ? '#fef2f2' : (subValue ? '#f0fdf4' : '#f9fafb'),
                                                            textAlign: 'center',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {subValue ? (
                                                            <span style={{ color: '#059669' }}>
                                                                ✓ {typeof subValue === 'string' ? subValue : subValue.name}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#6b7280' }}>
                                                                📎 {language === 'ar' ? 'اختر ملف' : 'Choose file'}
                                                            </span>
                                                        )}
                                                        <input
                                                            id={subFileInputId}
                                                            type="file"
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                const newData = [...ensuredData]
                                                                if (!newData[index]) newData[index] = {}
                                                                newData[index][subField.id] = e.target.files[0]
                                                                handleFieldChange(field.id, newData)
                                                            }}
                                                            accept=".jpg,.jpeg,.png,.pdf"
                                                        />
                                                    </div>
                                                ) : (
                                                    <input
                                                        type={subField.type === 'phone' ? 'tel' : 'text'}
                                                        className={`pfb-field-input ${error && (!subValue || subValue.toString().trim() === '') ? 'error' : ''}`}
                                                        value={subValue}
                                                        onChange={(e) => {
                                                            const newData = [...ensuredData]
                                                            if (!newData[index]) newData[index] = {}
                                                            newData[index][subField.id] = e.target.value
                                                            handleFieldChange(field.id, newData)
                                                        }}
                                                        placeholder={language === 'ar' ? (subField.placeholder_ar || '') : (subField.placeholder_en || '')}
                                                        style={{ width: '100%' }}
                                                    />
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                    )

                case 'note':
                    // Note field - display-only, no input required
                    const noteText = language === 'ar' ? (field.note_ar || field.label_ar) : (field.note_en || field.label_en)
                    const noteStyle = field.noteStyle || 'info' // info, warning, success
                    const noteColors = {
                        info: { bg: '#e0f2fe', border: '#0ea5e9', text: '#0369a1', icon: 'ℹ️' },
                        warning: { bg: '#fef3c7', border: '#f59e0b', text: '#b45309', icon: '⚠️' },
                        success: { bg: '#dcfce7', border: '#22c55e', text: '#166534', icon: '✓' },
                        error: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', icon: '!' }
                    }
                    const colors = noteColors[noteStyle] || noteColors.info
                    return (
                        <div
                            className="pfb-note-field"
                            style={{
                                padding: '16px 20px',
                                borderRadius: '8px',
                                backgroundColor: colors.bg,
                                border: `1px solid ${colors.border}`,
                                color: colors.text,
                                fontSize: '15px',
                                lineHeight: '1.6',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                marginBottom: '8px'
                            }}
                        >
                            <span style={{ fontSize: '18px' }}>{colors.icon}</span>
                            <span style={{ direction: isRTL ? 'rtl' : 'ltr' }}>{noteText}</span>
                        </div>
                    )

                default: // text
                    return (
                        <input
                            type="text"
                            className={`pfb-field-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            placeholder={placeholder}
                            minLength={field.min_length}
                            maxLength={field.max_length}
                        />
                    )
            }
        }

        // Single checkbox doesn't need a separate label
        if (field.type === 'checkbox' && (!field.options || field.options.length === 0)) {
            return (
                <div key={field.id} className={`pfb-field-group ${widthClass}`}>
                    {fieldContent()}
                    {error && <span className="pfb-field-error">{error}</span>}
                </div>
            )
        }

        // Note fields don't need a label - they are self-contained display elements
        if (field.type === 'note') {
            return (
                <div key={field.id} className={`pfb-field-group ${widthClass}`}>
                    {fieldContent()}
                </div>
            )
        }

        return (
            <div key={field.id} className={`pfb-field-group ${widthClass}`}>
                <label className={`pfb-field-label ${isRTL ? 'rtl' : ''}`}>
                    {label}
                    {field.required && <span className="required">*</span>}
                </label>
                {fieldContent()}
                {error && <span className="pfb-field-error">{error}</span>}
            </div>
        )
    }

    // Loading state
    if (isLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1a3a6b 0%, #2a7d62 100%)',
            }}>
                <div className="pfb-form-container">
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #e5e7eb',
                            borderTopColor: '#1a3a6b',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                            margin: '0 auto 20px',
                        }} />
                        <p style={{ color: '#6b7280' }}>Loading form...</p>
                    </div>
                </div>
            </div>
        )
    }

    // Error state
    if (isError || !form) {
        // Check if this is a login_required error from axios error response
        // Axios stores the response in error.response.data
        const errorData = error?.response?.data || formResponse?.data
        const isLoginRequired = errorData?.login_required === true

        if (isLoginRequired) {
            const redirectUrl = encodeURIComponent(window.location.pathname)
            return (
                <div style={{
                    minHeight: isEmbedded ? 'auto' : '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isEmbedded ? 'transparent' : 'linear-gradient(135deg, #1a3a6b 0%, #2a7d62 100%)',
                    padding: '20px',
                }}>
                    <div className="pfb-form-container" style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
                        <h2 style={{ color: '#111827', marginBottom: '10px' }}>
                            {language === 'ar' ? 'تسجيل الدخول مطلوب' : 'Login Required'}
                        </h2>
                        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                            {language === 'ar'
                                ? 'يرجى تسجيل الدخول للوصول إلى هذا النموذج'
                                : 'Please log in to access this form'}
                        </p>
                        <a
                            href={`/portal/login?redirect=${redirectUrl}`}
                            style={{
                                display: 'inline-block',
                                padding: '12px 32px',
                                background: 'linear-gradient(135deg, #1a3a6b 0%, #2a7d62 100%)',
                                color: '#fff',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                fontWeight: 600,
                                transition: 'transform 0.2s',
                            }}
                        >
                            {language === 'ar' ? 'تسجيل الدخول' : 'Log In'}
                        </a>
                    </div>
                </div>
            )
        }

        return (
            <div style={{
                minHeight: isEmbedded ? 'auto' : '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isEmbedded ? 'transparent' : 'linear-gradient(135deg, #1a3a6b 0%, #2a7d62 100%)',
                padding: '20px',
            }}>
                <div className="pfb-form-container" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>😕</div>
                    <h2 style={{ color: '#111827', marginBottom: '10px' }}>Form Not Found</h2>
                    <p style={{ color: '#6b7280' }}>
                        The form you're looking for doesn't exist or is no longer active.
                    </p>
                </div>
            </div>
        )
    }

    // Success state - render as bottom-fixed notification instead of replacing the form

    // Form
    return (
        <div style={{
            minHeight: isEmbedded ? 'auto' : '100vh',
            padding: isEmbedded ? '0' : '40px 20px',
            background: isEmbedded ? 'transparent' : `linear-gradient(135deg, ${formStyle.backgroundColor} 0%, ${formStyle.primaryColor} 100%)`,
        }}>
            <div
                className={`pfb-form-container ${isRTL ? 'rtl' : ''}`}
                style={{ maxWidth: `${formStyle.formWidth}px`, fontFamily: formStyle.fontFamily }}
            >
                {/* Form Header */}
                <div
                    className="pfb-form-header"
                    style={{ position: 'relative', background: `linear-gradient(135deg, ${formStyle.primaryColor} 0%, ${formStyle.headerGradientEnd} 100%)` }}
                >
                    <div style={{
                        position: 'absolute',
                        top: '15px',
                        [isRTL ? 'left' : 'right']: '15px',
                        zIndex: 10
                    }}>
                        <LanguageSwitcher sx={{ color: '#ffffff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }} />
                    </div>
                    {/* Header Logo - if configured */}
                    {formStyle.headerLogoUrl && (
                        <img
                            src={formStyle.headerLogoUrl}
                            alt="Form Logo"
                            className="pfb-header-logo"
                            style={{
                                maxHeight: '60px',
                                maxWidth: '200px',
                                marginBottom: '15px',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                    <h1 className="pfb-form-title">
                        {language === 'ar' ? (form.title_ar || form.title) : form.title}
                    </h1>
                    {(form.description || form.description_ar) && (
                        <p className="pfb-form-description">
                            {language === 'ar'
                                ? (form.description_ar || form.description)
                                : form.description}
                        </p>
                    )}
                </div>

                {/* Form Fields - Hide when submitted */}
                {!submitted ? (
                    <form onSubmit={handleSubmit}>
                        <div className="pfb-fields-container">
                            {fields.filter(shouldShowField).map((field) => renderField(field))}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="pfb-submit-button"
                            disabled={submitMutation.isPending || isEmailVerificationPending()}
                            style={{
                                background: isEmailVerificationPending()
                                    ? '#9ca3af'
                                    : `linear-gradient(135deg, ${formStyle.buttonColor} 0%, ${formStyle.primaryColor} 100%)`,
                                cursor: isEmailVerificationPending() ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {submitMutation.isPending && <span className="spinner"></span>}
                            {submitMutation.isPending
                                ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...')
                                : isEmailVerificationPending()
                                    ? (language === 'ar' ? 'يرجى التحقق من البريد الإلكتروني' : 'Please verify email first')
                                    : (language === 'ar' ? 'إرسال' : 'Submit')}
                        </button>
                    </form>
                ) : (
                    /* Success message - centered notification that replaces the form */
                    <div className="pfb-success-notification">
                        <div className="pfb-success-notification-content">
                            <div className="pfb-success-notification-icon">✓</div>
                            <div className="pfb-success-notification-text">
                                <h3 className="pfb-success-notification-title">
                                    {language === 'ar' ? 'شكراً لك!' : 'Thank You!'}
                                </h3>
                                <p className="pfb-success-notification-message">
                                    {language === 'ar'
                                        ? 'تم استلام طلبك بنجاح'
                                        : 'Your submission has been received successfully'}
                                </p>
                                <div className="pfb-success-notification-id">
                                    <span className="pfb-success-notification-label">
                                        {language === 'ar' ? 'رقم الطلب:' : 'Submission ID:'}
                                    </span>
                                    <span className="pfb-success-notification-value">
                                        {submissionId}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PublicForm
